import { Container, Sprite, type Texture } from "pixi.js";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";
import { resolveSymbolView } from "../../view/grid/SymbolView";
import type { SymbolWinEffect, SymbolWinAnimationRequest } from "./SymbolWinEffect";

interface ActiveSwitch {
  overlay: Sprite;
  base: Sprite;
  /** Bumped per switch on this cell; a stale in-flight fade stops writing the alphas. */
  gen: number;
}

/**
 * The frame "switch" symbol-win animation: a per-symbol overlay sprite (the symbol's LAST frame,
 * e.g. `H1-2`) fades IN while the base symbol fades OUT, so the cell switches base → alt (only
 * the alt shows — a switch, not a blend).
 *
 * Two modes (`hold`):
 *  - `hold: false` (default): base → alt → hold → back. Each `play()` (every win-cycle pass)
 *    restarts a fresh switch; an optional `switchBackEffect` fires at the switch-back moment.
 *  - `hold: true`: switch to the alt and STAY on it through the win cycles until the next spin;
 *    a re-fire is a no-op. `holdSeconds` / `fadeOutSeconds` / `switchBackEffect` are unused.
 *
 * The fades run on engine time, so the switch pauses / frame-steps / speed-scales with the rest of
 * the presentation. Cancellation is a per-cell generation token: `stopAll()` / a re-fire / `finish()`
 * bumps it and any stale in-flight fade stops writing. The overlay is a SIBLING of the cell's
 * `symbolSprite` (same parent and transform, drawn after it) so the two alphas are independent.
 * `stopAll()` drops the overlays and restores the base symbols — nothing leaks onto the pooled cell.
 */
export class OverlayFadeSymbolWinEffect implements SymbolWinEffect {
  private readonly framesBySymbol: ReadonlyMap<string, readonly Texture[]>;
  private readonly fadeInSeconds: number;
  private readonly startDelayMilliseconds: number;
  private readonly baseFadeOutDelaySeconds: number;
  private readonly hold: boolean;
  private readonly holdSeconds: number;
  private readonly fadeOutSeconds: number;
  private readonly switchBackEffect: SymbolWinEffect | null;
  /** hold mode only: a switched cell's view is marked pool-unsafe, so the pool destroys (never recycles) the overlay-bearing view. */
  private readonly markDirtyOnHold: boolean;
  private readonly taskRunner: PresentationTaskRunner;
  /** cellNode → its active switch (in flight in switch-back mode; persistent in hold mode). */
  private readonly active: Map<Container, ActiveSwitch> = new Map();
  private generation = 0;

  public constructor(input: {
    framesBySymbol: ReadonlyMap<string, readonly Texture[]>;
    fadeInSeconds: number;
    taskRunner: PresentationTaskRunner;
    /** Per-cell lead-in (ms) before the cross-fade starts — 0 = immediate. */
    startDelayMilliseconds?: number;
    /**
     * HOLD mode: seconds the OUTGOING base frame stays at full alpha before it fades out (the
     * incoming overlay's head start, so the new frame covers the cell before the old one leaves).
     * 0 = both fade together. Clamps to `fadeInSeconds` in effect.
     */
    baseFadeOutDelaySeconds?: number;
    /** true = switch and STAY on the alt frame; false (default) = switch → hold → back. */
    hold?: boolean;
    /** Switch-back mode only: the alt hold before reverting, and the revert fade. */
    holdSeconds?: number;
    fadeOutSeconds?: number;
    /** Switch-back mode only: fired at the switch-back moment (e.g. a little pulse). */
    switchBackEffect?: SymbolWinEffect | null;
    markDirtyOnHold?: boolean;
  }) {
    this.framesBySymbol = input.framesBySymbol;
    this.fadeInSeconds = input.fadeInSeconds;
    this.taskRunner = input.taskRunner;
    this.startDelayMilliseconds = input.startDelayMilliseconds ?? 0;
    this.baseFadeOutDelaySeconds = input.baseFadeOutDelaySeconds ?? 0;
    this.hold = input.hold ?? false;
    this.holdSeconds = input.holdSeconds ?? 0;
    this.fadeOutSeconds = input.fadeOutSeconds ?? 0;
    this.switchBackEffect = input.switchBackEffect ?? null;
    this.markDirtyOnHold = input.markDirtyOnHold ?? false;
  }

  public play(request: SymbolWinAnimationRequest): void {
    const frames = this.framesBySymbol.get(request.symbolId);
    if (!frames || frames.length === 0) return;
    const cell = request.cellNode;
    if (cell.destroyed) return;
    if (this.hold && this.active.has(cell)) return; // hold: already switched — stay put

    const view = resolveSymbolView(cell);
    const base = view?.symbolSprite ?? null;
    if (!view || !base || base.destroyed || !base.parent) return;

    if (!this.hold) this.finish(cell); // switch-back: restart any in-flight switch

    const overlay = new Sprite({ texture: frames[frames.length - 1], label: "SymbolWinOverlay" });
    overlay.anchor.copyFrom(base.anchor);
    overlay.position.copyFrom(base.position);
    overlay.scale.copyFrom(base.scale);
    overlay.alpha = 0;
    base.parent.addChildAt(overlay, base.parent.getChildIndex(base) + 1);

    const gen = ++this.generation;
    this.active.set(cell, { overlay, base, gen });

    if (this.hold) {
      if (this.markDirtyOnHold) view.markPoolUnsafe();
      void this.runHold(cell, base, overlay, gen);
      return;
    }
    void this.runSwitchBack(cell, base, overlay, request, gen);
  }

  public stopAll(): void {
    for (const cell of Array.from(this.active.keys())) this.finish(cell);
    this.switchBackEffect?.stopAll();
  }

  /** Hold mode: the overlay fades in while the base holds, then the base fades out; the switch stays. */
  private async runHold(cell: Container, base: Sprite, overlay: Sprite, gen: number): Promise<void> {
    const startDelaySec = this.startDelayMilliseconds / 1000;
    if (startDelaySec > 0) {
      await this.taskRunner.sleep(startDelaySec);
      if (!this.isCurrent(cell, gen)) return;
    }
    void this.fade(cell, gen, overlay, 0, 1, this.fadeInSeconds);
    if (this.baseFadeOutDelaySeconds > 0) {
      await this.taskRunner.sleep(this.baseFadeOutDelaySeconds);
      if (!this.isCurrent(cell, gen)) return;
    }
    await this.fade(cell, gen, base, base.alpha, 0, this.fadeInSeconds);
  }

  /** Switch-back mode: base → alt → hold → back, with an optional switch-back pulse. */
  private async runSwitchBack(cell: Container, base: Sprite, overlay: Sprite, request: SymbolWinAnimationRequest, gen: number): Promise<void> {
    const startDelaySec = this.startDelayMilliseconds / 1000;
    if (startDelaySec > 0) {
      await this.taskRunner.sleep(startDelaySec);
      if (!this.isCurrent(cell, gen)) return;
    }
    // The base timeline: fade out → hold → fade back in (concurrent with the overlay's).
    void (async () => {
      await this.fade(cell, gen, base, 1, 0, this.fadeInSeconds);
      if (!this.isCurrent(cell, gen)) return;
      if (this.holdSeconds > 0) {
        await this.taskRunner.sleep(this.holdSeconds);
        if (!this.isCurrent(cell, gen)) return;
      }
      await this.fade(cell, gen, base, 0, 1, this.fadeOutSeconds);
    })();
    // The overlay timeline: fade in → hold → switch-back pulse → fade out → finish.
    await this.fade(cell, gen, overlay, 0, 1, this.fadeInSeconds);
    if (!this.isCurrent(cell, gen)) return;
    if (this.holdSeconds > 0) {
      await this.taskRunner.sleep(this.holdSeconds);
      if (!this.isCurrent(cell, gen)) return;
    }
    this.switchBackEffect?.play(request);
    await this.fade(cell, gen, overlay, 1, 0, this.fadeOutSeconds);
    if (!this.isCurrent(cell, gen)) return;
    this.finish(cell);
  }

  /** A linear alpha fade on engine time, guarded by the cell's token. */
  private async fade(cell: Container, gen: number, sprite: Sprite, from: number, to: number, seconds: number): Promise<void> {
    if (!this.isCurrent(cell, gen)) return;
    await this.taskRunner.tweenProgress(
      seconds,
      (t01) => {
        if (this.isCurrent(cell, gen) && !sprite.destroyed) sprite.alpha = from + (to - from) * t01;
      },
      undefined,
      { easing: "linear" },
    );
  }

  private isCurrent(cell: Container, gen: number): boolean {
    return !cell.destroyed && this.active.get(cell)?.gen === gen;
  }

  /**
   * Stop the switch on a cell: invalidate its in-flight fades, drop the overlay, restore the base.
   * A pool-unsafe (held) cell keeps its overlay and faded base: a reel drop-out carries the alt frame
   * out WITH the symbol and the pool destroys the view when it recycles.
   */
  private finish(cell: Container): void {
    const sw = this.active.get(cell);
    if (!sw) return;
    this.active.delete(cell); // invalidates in-flight fades (isCurrent → false)
    const dirty = !(resolveSymbolView(cell)?.isPoolReusable() ?? true);
    if (dirty) return;
    if (!sw.overlay.destroyed) sw.overlay.destroy();
    if (!sw.base.destroyed) sw.base.alpha = 1;
  }
}
